/**
 * THE allowlist: the complete, field-by-field catalogue of what a settings
 * bundle may contain. Nothing travels unless it is named here.
 *
 * An object-valued field must declare either a nested `fields` allowlist or a
 * written `deep` reason — `collectStoreSlice` THROWS on an undeclared object,
 * so "someone adds a field to an already-exported object" is a build-time-loud
 * failure rather than a silent leak. That is the trap `spatialSettings` is a
 * live example of: it holds `customStyleUrl`, which can carry a map-provider
 * API key, so only `basemapEnabled` is listed below.
 */
import type { SettingsBundleGroupId } from './settingsBundleManifest';

/** Leaf value type. Enforced on IMPORT — an untrusted file may lie. */
export type ExportedFieldType = 'string' | 'number' | 'boolean';

export type ExportedField = {
  name: string;
  /** Required on leaves; absent on object fields. Rejects wrong-typed imports. */
  type?: ExportedFieldType;
  /** The value is an object; only these keys travel (recursive). */
  fields?: ExportedField[];
  /** Take the value wholesale. The string is the written justification. */
  deep?: string;
};

export type ExportedStoreSlice = {
  /** The `persist({ name })` key in `localStorage`. */
  store: string;
  fields: ExportedField[];
};

export type SettingsBundleGroup = {
  id: SettingsBundleGroupId;
  label: string;
  description: string;
  /** OFF by default in the export panel (D7: snippets are opt-in). */
  optIn?: boolean;
  slices: ExportedStoreSlice[];
};

export const SETTINGS_BUNDLE_GROUPS: SettingsBundleGroup[] = [
  {
    id: 'ui',
    label: 'Appearance & layout',
    description: 'Theme, tab orientation, sidebar and terminal panel sizes, grid detail prefs.',
    slices: [
      {
        store: 'sql-studio-app-store',
        fields: [
          { name: 'themeMode', type: 'string' },
          { name: 'editorColorScheme', type: 'string' },
          {
            name: 'customEditorColors',
            fields: [
              {
                name: 'light',
                fields: [
                  { name: 'keyword', type: 'string' },
                  { name: 'identifier', type: 'string' },
                  { name: 'number', type: 'string' },
                  { name: 'string', type: 'string' },
                  { name: 'string.double', type: 'string' },
                  { name: 'operator', type: 'string' },
                  { name: 'comment', type: 'string' },
                  { name: 'comment.quote', type: 'string' },
                  { name: 'predefined', type: 'string' },
                ],
              },
              {
                name: 'dark',
                fields: [
                  { name: 'keyword', type: 'string' },
                  { name: 'identifier', type: 'string' },
                  { name: 'number', type: 'string' },
                  { name: 'string', type: 'string' },
                  { name: 'string.double', type: 'string' },
                  { name: 'operator', type: 'string' },
                  { name: 'comment', type: 'string' },
                  { name: 'comment.quote', type: 'string' },
                  { name: 'predefined', type: 'string' },
                ],
              },
            ],
          },
          { name: 'tabOrientation', type: 'string' },
          { name: 'verticalTabsWidth', type: 'number' },
          { name: 'objectExplorerGrouping', type: 'string' },
          { name: 'expandedJsonView', type: 'string' },
          { name: 'rowDetailsHideNulls', type: 'boolean' },
          { name: 'rowDetailsDensity', type: 'string' },
          { name: 'rowDetailsGroupByCategory', type: 'boolean' },
          { name: 'fkOpenDefault', type: 'string' },
          { name: 'numericGrouping', type: 'string' },
          { name: 'insertSkipIdentity', type: 'boolean' },
        ],
      },
      {
        store: 'sql-studio-sidebar',
        fields: [
          { name: 'isCollapsed', type: 'boolean' },
          { name: 'width', type: 'number' },
          { name: 'sidebarSplitRatio', type: 'number' },
          { name: 'explorerCollapsed', type: 'boolean' },
          { name: 'scriptsCollapsed', type: 'boolean' },
        ],
      },
      {
        store: 'sql-studio-terminal',
        fields: [{ name: 'height', type: 'number' }, { name: 'isVisible', type: 'boolean' }],
      },
      {
        // B9's Dependency Viewer graph preference, landed on a branch that
        // predates this manifest. A single display boolean — "dim the
        // non-matching nodes instead of hiding them" — with no connection,
        // path or machine binding, so it belongs in Appearance & layout
        // rather than in the not-transferred list.
        store: 'jam-sql-dependency-graph-filter-prefs',
        fields: [{ name: 'dimNonMatchesInsteadOfHiding', type: 'boolean' }],
      },
    ],
  },
  {
    id: 'editor',
    label: 'Editor & execution',
    description: 'Word wrap, tab size, inline execute bar, IntelliSense toggles, query timeout.',
    slices: [
      {
        store: 'sql-studio-app-store',
        fields: [
          {
            name: 'editorSettings',
            fields: [
              { name: 'wordWrap', type: 'string' },
              { name: 'formatOnExecute', type: 'boolean' },
              { name: 'tabSize', type: 'number' },
              { name: 'showInlineExecuteBar', type: 'boolean' },
            ],
          },
          {
            name: 'mssqlIntelliSense',
            fields: [
              { name: 'enableIntelliSense', type: 'boolean' },
              { name: 'enableSuggestions', type: 'boolean' },
              { name: 'enableErrorChecking', type: 'boolean' },
              { name: 'enableQuickInfo', type: 'boolean' },
              { name: 'lowerCaseSuggestions', type: 'boolean' },
            ],
          },
          // `pgsqlToolsSettings.customBinPath` is deliberately absent: an
          // absolute path to another machine's PostgreSQL bin directory is
          // useless at best and misleading at worst on the target machine.
          { name: 'queryExecutionSettings', fields: [{ name: 'timeoutSeconds', type: 'number' }] },
        ],
      },
    ],
  },
  {
    id: 'ai',
    label: 'AI & terminal',
    description: 'AI workspace, execution-history and agent-context preferences.',
    slices: [
      {
        store: 'sql-studio-app-store',
        fields: [
          {
            name: 'aiTerminalSettings',
            // `nudges` (D7: dismissal flags are dropped) and
            // `persistentWorkspaces` (a connection-id-keyed folder registry
            // bound to this machine) are deliberately absent.
            fields: [
              {
                name: 'aiWorkspace',
                fields: [
                  { name: 'folder', type: 'string' },
                  { name: 'syncDebounceMs', type: 'number' },
                  { name: 'lockTimeoutMinutes', type: 'number' },
                  { name: 'cleanupOnClose', type: 'boolean' },
                  { name: 'exportSchemaOnOpen', type: 'boolean' },
                  { name: 'confirmBeforeSetup', type: 'boolean' },
                ],
              },
              {
                name: 'history',
                fields: [{ name: 'enabled', type: 'boolean' }, { name: 'maxEntries', type: 'number' }, { name: 'maxAge', type: 'string' }],
              },
              { name: 'agentContext', fields: [{ name: 'enabled', type: 'boolean' }, { name: 'includeSchema', type: 'boolean' }] },
              { name: 'autoLaunch', fields: [{ name: 'enabled', type: 'boolean' }, { name: 'agent', type: 'string' }] },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'spatial',
    label: 'Spatial maps',
    description: 'Basemap preference. The custom style URL stays on this machine — it can carry an API key.',
    slices: [
      {
        store: 'sql-studio-app-store',
        fields: [{ name: 'spatialSettings', fields: [{ name: 'basemapEnabled', type: 'boolean' }] }],
      },
    ],
  },
  {
    id: 'toolbar',
    label: 'Toolbar layout',
    description:
      'Which buttons sit on the main toolbar, in what order, and which are hidden. ' +
      'Imports replace the toolbar on this machine rather than blending with it.',
    slices: [
      {
        store: 'sql-studio-app-store',
        fields: [
          {
            name: 'toolbarCustomization',
            deep:
              'A `ToolbarLayout` of stable item ids only — no free text, no paths. ' +
              'Validated by `validateToolbarLayout` before it is applied on import.',
          },
        ],
      },
    ],
  },
  {
    id: 'keyboardShortcuts',
    label: 'Keyboard shortcuts',
    description:
      'Your shortcut scheme and the shortcuts you changed, added or removed. ' +
      'Imports replace your shortcuts on this machine rather than blending with them.',
    slices: [
      {
        store: 'jam-sql-keybindings',
        fields: [
          { name: 'scheme', type: 'string' },
          {
            name: 'overrides',
            deep:
              'A map of keybinding command ids to chord strings — no free text, no paths. ' +
              'Normalized on import by `normalizeOverrides`, the same function the store runs on ' +
              'hydration: `[]` (explicitly unbound) is kept, unreadable chords are dropped, and ' +
              'ids from another app version are kept for the Settings tab to list.',
          },
        ],
      },
    ],
  },
  {
    id: 'beta',
    label: 'Beta features',
    description:
      'Which beta features you switched on or off. Only the choices you made travel — anything you never touched follows this build\u2019s defaults.',
    slices: [
      {
        store: 'sql-studio-app-store',
        fields: [
          {
            name: 'betaFeatures',
            deep:
              'A map of stable beta-feature ids to booleans \u2014 no free text, no paths. ' +
              'Normalized by `normalizeBetaFeatureOverrides` on import, which drops any id ' +
              'this build no longer knows and any non-boolean value.',
          },
        ],
      },
    ],
  },
  {
    id: 'azure-storage',
    label: 'Azure Storage',
    description: 'Transfer job concurrency and resumable upload block settings.',
    slices: [
      {
        store: 'sql-studio-app-store',
        fields: [
          {
            name: 'azureStorageTransferSettings',
            fields: [
              { name: 'maxConcurrentJobs', type: 'number' },
              { name: 'blockConcurrency', type: 'number' },
              { name: 'blockSizeMiB', type: 'number' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'snippets',
    label: 'SQL snippets',
    description: 'Your saved snippets. Off by default — snippet bodies are SQL you wrote.',
    optIn: true,
    slices: [
      {
        store: 'sql-studio-sql-snippets',
        fields: [
          {
            name: 'snippetsByEngine',
            deep:
              'Engine-keyed lists of user-authored snippets. Shared wholesale only ' +
              'because this group is opt-in and off by default (D7).',
          },
        ],
      },
    ],
  },
];
